import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useAuthStore } from '../shared/store/useAuthStore'

// frontend-restructuring §0.8f / Fase 3 (03.1-auth-store.md) — the stable auth API that
// feature **containers** use.
//
// Backed by `useAuthStore` (zustand) since Fase 3 — selectors here are real per-slice
// reactivity now, not just a shared read of one big object: a consumer of `useCurrentUser()`
// does NOT re-render when `user.status`/`currentSpace.icon` changes, because the selector picks
// out only the fields it names and `useShallow` compares just those values across renders, not
// the whole `user` object's reference (03.1-auth-store.md §1 — the original selector-over-Context
// design didn't actually achieve this; a zustand selector that returns the *whole* `user` object
// wouldn't either).
//
// A **presentational** component never calls these (eslint forbids importing `context/*` and
// `shared/store/*`) — it receives what it needs as props from its container.

/**
 * The current user's identity — name/image/dbId/id only (README §2.4's own definition,
 * corrected: `uid` is a dead field, never set past the logged-out default). Consumers that need
 * other fields (`role`, `spaces`, `description`, etc.) use the `useUser()` shim for now — this
 * hook intentionally stays narrow so it can guarantee the acceptance bullet, not because those
 * other fields don't matter.
 */
export function useCurrentUser() {
    return useAuthStore(useShallow((s) => ({
        name: s.user.name, image: s.user.image, dbId: s.user.dbId, id: s.user.id,
    })))
}

/**
 * The **whole** `user` object — for the handful of feature containers/hooks that genuinely read
 * broad fields (`role`, `spaces`, `timezone`, `calendar_personal`, `map_pos_x`, …) or thread the
 * whole object to a child that does. Re-renders on any `user` update (same reactivity the old
 * `useUser()` shim gave — this replaced it in Phase 4.4). Prefer `useCurrentUser()` when you only
 * need name/image/dbId/id; a **presentational** component never calls this (boundary lint),
 * it receives `user` as a prop from its container.
 */
export function useAuthUser() {
    return useAuthStore((s) => s.user)
}

/** The current space + the ways to change it. */
export function useCurrentSpace() {
    const currentSpace = useAuthStore((s) => s.user?.currentSpace ?? null)
    const updateCurrentSpace = useAuthStore((s) => s.updateCurrentSpace)
    const updateUser = useAuthStore((s) => s.updateUser)
    return useMemo(
        () => ({
            currentSpace,
            updateCurrentSpace,
            // `SpaceLayout` sets the space via `updateUser({ currentSpace })` — keep that path.
            setCurrentSpace: (space) => updateUser({ currentSpace: space }),
        }),
        [currentSpace, updateCurrentSpace, updateUser],
    )
}

/** Session lifecycle flags — is the app still booting, is the space resolved, is there a DB session. */
export function useSessionStatus() {
    return useAuthStore(useShallow((s) => ({
        loading: s.loading, spaceCheckDone: s.spaceCheckDone, isDbConnected: !!s.user?.isDbConnected,
    })))
}

/** Auth actions — referentially stable (each is a plain function on the store, never recreated). */
export function useAuthActions() {
    return useAuthStore(useShallow((s) => ({
        login: s.login, logout: s.logout, updateUser: s.updateUser, updateUserProfile: s.updateUserProfile,
        changeCredentials: s.changeCredentials, updateCurrentSpace: s.updateCurrentSpace,
        checkNickAvailability: s.checkNickAvailability, uploadProfileImage: s.uploadProfileImage,
    })))
}
